import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigService,
    CONNECTION_ERROR_CODES,
    SynchronizationService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of, Subject } from 'rxjs';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { OdataRequestNotificationService } from './odata-request-notification.service';
import RequestNotificationModel from './request-notification.model';

@Component({
    selector: 'pace-request-notification',
    templateUrl: './request-notification.component.html',
    styleUrls: ['./request-notification.component.scss'],
})
export class RequestNotificationComponent implements OnInit, OnDestroy {
    public model: RequestNotificationModel;
    public isLoading = false;
    public formChildren: FormArray;
    public requestNotificationForm: FormGroup;
    private destroyTrigger = new Subject<void>();
    public showStartImmediately = true;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        private readonly appNotificationService: AppNotificationService,
        public readonly modalInstance: BsModalRef,
        private readonly odataRequestNotificationService: OdataRequestNotificationService,
        private readonly synchronizationService: SynchronizationService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly configService: ConfigService,
    ) {
        this.model = new RequestNotificationModel();
    }

    public ngOnInit(): void {
        this.showStartImmediately = this.configService.getShowStartImmediately();
        this.formChildren = this.formBuilder.array([]);
        this.requestNotificationForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
    }

    public ngOnDestroy(): void {
        this.destroyTrigger.next();
    }

    public validateForm(): void {
        this.model.resetModelOptions();
        this.formChildren.controls.forEach((control) =>
            control.updateValueAndValidity({ onlySelf: false, emitEvent: true }),
        );
        this.changeDetector.detectChanges();
    }

    public requestNotification(): void {
        this.isLoading = true;
        this.odataRequestNotificationService
            .requestNotification(this.model)
            .pipe(
                mergeMap(() => {
                    this.modalInstance.hide();
                    this.log.debug('RequestNotificationComponent -> sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.isLoading = false;
                    this.log.error(`RequestNotificationComponent: requestNotification: Error:`, error);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                            : <string>this.translate.instant('DASHBOARD_SIDEMENU_CREATE_NOTIFICATION_ERROR_TITLE'),
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                            : JSON.stringify(error),
                    );
                    return of(undefined);
                }),
                takeUntil(this.destroyTrigger),
            )
            .subscribe();
    }
}
